<template>
  <div class="material-label-print" :class="{ 'material-label-print--inline': copiesInPreview }">
    <div class="material-print-actions">
      <label v-if="!copiesInPreview" class="material-print-copies">
        <span>打印份数</span>
        <input
          v-model="copies"
          type="number"
          inputmode="numeric"
          min="1"
          max="100"
          step="1"
          :aria-label="material.MATNO + ' 打印份数'"
          :disabled="disabled || isPreparing || isPrinting"
        />
      </label>
      <van-button
        plain
        type="primary"
        size="small"
        class="material-print-button"
        :disabled="disabled || isPreparing || isPrinting"
        :loading="isPreparing"
        loading-text="生成中"
        @click="preparePreview"
      >打印</van-button>
    </div>

    <van-popup
      v-model="previewVisible"
      position="bottom"
      get-container="body"
      class="material-print-popup"
      :style="{ width: '100%', height: '100%', background: '#F7F8FA' }"
      :close-on-click-overlay="!isPrinting"
    >
      <div class="material-print-preview">
        <van-nav-bar title="物料标签预览">
          <template #left>
            <button type="button" class="material-preview-action" :disabled="isPrinting" @click="closePreview">取消</button>
          </template>
          <template #right>
            <button type="button" class="material-preview-action" :disabled="isPrinting || isRefreshing || !previewUrl" @click="confirmPrint">
              {{ isPrinting ? '发送中…' : sentCopies ? '继续打印' : '确认打印' }}
            </button>
          </template>
        </van-nav-bar>
        <div class="material-preview-content">
          <div v-if="copiesInPreview" class="material-preview-options">
            <label class="material-print-location">
              <span>仓位</span>
              <input
                :value="locationDraft"
                type="text"
                placeholder="请输入仓位"
                :aria-label="material.MATNO + ' 仓位'"
                :disabled="isPrinting || sentCopies > 0"
                @input="updateLocation($event.target.value)"
              />
            </label>
            <label class="material-print-copies">
              <input
                v-model="copies"
                type="number"
                inputmode="numeric"
                min="1"
                max="100"
                step="1"
                :aria-label="material.MATNO + ' 打印份数'"
                :disabled="isPrinting || sentCopies > 0"
              />
              <span>份</span>
            </label>
          </div>
          <p v-if="isRefreshing" class="material-preview-meta" role="status">正在更新预览…</p>
          <img v-if="previewUrl" :src="previewUrl" class="material-preview-image" :alt="previewLabel ? previewLabel.code + ' 物料标签' : '物料标签'" />
          <p v-if="isPrinting || sentCopies" class="material-preview-progress" role="status" aria-live="polite">
            已发送 {{ sentCopies }} / {{ previewCopies }} 份{{ isPrinting ? '，请等待打印机定位…' : '' }}
          </p>
          <p v-if="printError" class="material-preview-error" role="alert">{{ printError }}</p>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import {
  isSupported,
  getUnsupportedReason,
  renderPreview,
  printMaterialBarcode
} from '@/api/materialBarcodePrint'

export default {
  name: 'StockCheckMaterialLabelPrint',
  props: {
    material: { type: Object, required: true },
    warehouseName: { type: String, default: '' },
    copiesInPreview: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      copies: '1',
      previewCopies: 1,
      previewVisible: false,
      previewLabel: null,
      previewUrl: '',
      locationDraft: '',
      previewRevision: 0,
      isPreparing: false,
      isRefreshing: false,
      isPrinting: false,
      sentCopies: 0,
      printError: '',
      isDisposed: false
    }
  },
  beforeDestroy() {
    this.isDisposed = true
    this.previewRevision++
    if (this.isPreparing || this.isRefreshing || this.isPrinting) this.$emit('busy', false)
  },
  methods: {
    getCopyCount() {
      const value = String(this.copies).trim()
      const copies = Number(value)
      if (!/^\d+$/.test(value) || !Number.isInteger(copies) || copies < 1 || copies > 100) {
        this.$dialog.alert({ title: '打印份数', message: '请输入 1 到 100 的整数' })
        return null
      }
      return copies
    },
    async preparePreview() {
      if (this.disabled || this.isPreparing || this.isRefreshing || this.isPrinting) return
      const copies = this.copiesInPreview ? 1 : this.getCopyCount()
      if (copies === null) return
      const code = String(this.material.MATNO || '').trim()
      if (!code) {
        this.$dialog.alert({ title: '无法打印', message: '该物料没有物料编码，无法生成二维码' })
        return
      }
      // 仅复制当前物料和仓库的标签字段，打印份数保留在组件内。
      const label = {
        code,
        matnm: this.material.MATNM || '',
        specnm: this.material.MATDESC || '',
        stknm: this.warehouseName || this.material.STKNM || '',
        plcnm: this.material.PLCNM || ''
      }
      this.isPreparing = true
      this.$emit('busy', true)
      this.previewLabel = null
      this.previewUrl = ''
      this.printError = ''
      try {
        const url = await renderPreview(label)
        if (this.isDisposed) return
        this.previewLabel = label
        this.previewUrl = url
        this.locationDraft = String(label.plcnm)
        this.previewCopies = copies
        this.sentCopies = 0
        this.previewVisible = true
      } catch (error) {
        this.$dialog.alert({ title: '生成预览失败', message: error.message || '请重试' })
      } finally {
        this.isPreparing = false
        this.$emit('busy', false)
      }
    },
    async updateLocation(value) {
      if (!this.copiesInPreview || this.isPrinting || this.sentCopies > 0 || !this.previewLabel) return
      this.locationDraft = String(value)
      const label = { ...this.previewLabel, plcnm: this.locationDraft.trim() }
      const revision = ++this.previewRevision
      this.isRefreshing = true
      this.previewUrl = ''
      this.printError = ''
      this.$emit('busy', true)
      try {
        const url = await renderPreview(label)
        if (this.isDisposed || revision !== this.previewRevision) return
        this.previewLabel = label
        this.previewUrl = url
      } catch (error) {
        if (!this.isDisposed && revision === this.previewRevision) {
          this.printError = '预览更新失败：' + (error.message || '请修改仓位后重试')
        }
      } finally {
        if (!this.isDisposed && revision === this.previewRevision) {
          this.isRefreshing = false
          this.$emit('busy', false)
        }
      }
    },
    closePreview() {
      if (this.isPrinting) return
      this.previewVisible = false
      this.previewRevision++
      if (this.isRefreshing) {
        this.isRefreshing = false
        this.$emit('busy', false)
      }
    },
    async confirmPrint() {
      if (this.isPrinting || this.isRefreshing || !this.previewLabel || !this.previewUrl) return
      if (this.copiesInPreview && this.sentCopies === 0) {
        const copies = this.getCopyCount()
        if (copies === null) return
        this.previewCopies = copies
      }
      if (!isSupported()) {
        this.$dialog.alert({ title: '不支持蓝牙打印', message: getUnsupportedReason() })
        return
      }
      this.isPrinting = true
      this.printError = ''
      this.$emit('busy', true)
      try {
        for (let i = this.sentCopies; i < this.previewCopies && !this.isDisposed; i++) {
          // 第一次调用直接发起连接，保留确认按钮的用户点击手势。
          await printMaterialBarcode(this.previewLabel, { previewUrl: this.previewUrl })
          this.sentCopies++
          if (this.sentCopies < this.previewCopies) {
            // 蓝牙写入完成不等于打印完成，沿用待盘点页面的定位等待时间。
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
        if (this.isDisposed) return
        this.previewVisible = false
        this.$toast.success('已发送 ' + this.sentCopies + ' 份，请确认纸面打印结果')
      } catch (error) {
        this.printError = error.name === 'NotFoundError'
          ? '已取消蓝牙选择，可重新确认打印'
          : '已发送 ' + this.sentCopies + '/' + this.previewCopies + ' 份，发送已停止。' +
            (error.message || '连接打印机失败') + '。请核对纸面后再继续。'
      } finally {
        this.isPrinting = false
        this.$emit('busy', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.material-print-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  padding: 8px 0;
  border-top: 1px solid #F0F0F0;
}

.material-print-copies {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #666666;

  input {
    box-sizing: border-box;
    width: 52px;
    height: 30px;
    padding: 0 6px;
    border: 1px solid #E5E5E5;
    border-radius: 4px;
    background: #F7F8FA;
    color: #333333;
    font-size: 13px;
    text-align: center;

    &:focus {
      outline: 2px solid #235EFF;
      outline-offset: 1px;
    }
  }
}

.material-print-button {
  min-width: 60px;
  height: 30px;
  padding: 0 10px;
  font-size: 13px;
}

.material-label-print--inline {
  flex-shrink: 0;

  .material-print-actions {
    margin: 0;
    padding: 0;
    border: 0;
  }

  .material-print-button {
    min-width: 52px;
    height: 28px;
    padding: 0 8px;
    font-size: 12px;
  }
}

.material-preview-options {
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  max-width: 560px;
  margin: 0 auto 16px;
  padding: 12px;
  background: #FFFFFF;
  border-radius: 6px;
  font-size: 13px;
  line-height: 22px;
  text-align: left;
  color: #333333;
  overflow-wrap: anywhere;

  .material-print-copies { flex-shrink: 0; gap: 6px; }
}

.material-print-location {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;

  span { flex-shrink: 0; }

  input {
    box-sizing: border-box;
    flex: 1;
    min-width: 0;
    width: 100%;
    height: 36px;
    padding: 0 8px;
    border: 1px solid #E5E5E5;
    border-radius: 4px;
    background: #F7F8FA;
    color: #333333;
    font-size: 14px;

    &:focus {
      outline: 2px solid #235EFF;
      outline-offset: 1px;
    }
  }
}

.material-print-preview {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.material-preview-action {
  min-height: 44px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: #235EFF;
  font-size: 14px;
  cursor: pointer;

  &:disabled { color: #999999; }
}

.material-preview-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 12px;
  text-align: center;
}

.material-preview-meta,
.material-preview-progress {
  margin: 0 0 16px;
  font-size: 14px;
  color: #666666;
}

.material-preview-image {
  display: block;
  width: 560px;
  max-width: 100%;
  height: auto;
  margin: 0 auto 16px;
  background: #FFFFFF;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.material-preview-error {
  max-width: 560px;
  margin: 0 auto;
  color: #D93025;
  font-size: 14px;
  line-height: 22px;
  text-align: left;
}
</style>
